import { Deserializer } from 'jsonapi-serializer';
import { EnrolmentAudit, EnrolmentAuditWrapper } from '../models/EnrolmentAudit';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { getDateTimeWithoutMillis } from '../utilities/DateUtils';
import { getActionName, getEnrolmentType, getEnrolmentField, getEnrolmentValue } from '../utilities/AuditUtils';
import { APIConnector } from './api/APIConnector';
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { Translator } from "../contexts/translator";
import { StoreEntityResult } from '../models/services/StoreEntityResult';

const apiConnector = new APIConnector();

/*
Audit records for a specific person.
E.g. edits on Master / MasterGroup / MasterFloor / MasterAsset / Tag / MasterExpiry / MasterDetail / etc.
*/
export const getAuditForPerson = async (masterId: string, listParameters?: ListQuery, translate?: Translator): Promise<EnrolmentAuditWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `auditPerson?${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, records: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }
                
                const auditRecords: EnrolmentAudit[] = records.map((personAudit) => buildPersonAudit(personAudit, translate));
                return resolve({ rows: auditRecords, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/*
  Build the audit record for a specific person.
*/
const buildPersonAudit: (value: any, translate) => EnrolmentAudit = (apiPersonAudit, translate?: Translator): EnrolmentAudit => {
    return {
        id: apiPersonAudit['id'],
        name: apiPersonAudit['type'],
        dateTime: getDateTimeWithoutMillis(apiPersonAudit['date-time-local']),
        operator: apiPersonAudit['operator'],
        action: getActionName(apiPersonAudit['type'], apiPersonAudit['action'], translate),
        type: getEnrolmentType(apiPersonAudit['type'], apiPersonAudit['action'], translate),
        field: getEnrolmentField(apiPersonAudit['field'], translate),
        valueOld: getEnrolmentValue(apiPersonAudit['value-old'], apiPersonAudit['field'], translate),
        valueNew: getEnrolmentValue(apiPersonAudit['value-new'], apiPersonAudit['field'], translate)
    };
};

export const auditRunReport = async (reportId: string | number): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "auditRunReport",
            attributes: {
                id: reportId
            }
        }

        let response;

        await apiConnector.sendData('', true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error))
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    const content = await response.json();
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) ?? genericError })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return Promise.reject(processServerError(error))
    }
}